import Content from "./content";
import Dropdown from "~/ui/dropdown";
import Tooltip, { ETooltipFlow } from "~/ui/tooltip";
import { isFirmFromPoi } from "~/util/review";
import ReportForm from "~/report/index";
import { getSuggestDataOptions } from "~/report/options";
import { reportWaitForLogin, reportAnalytics } from "~/report/util";
import { isPointInStatePolygon } from "~/util/state-polygon";
import { _ } from "~/i18n";
import { createDeepLTranslate } from "~/ui/deepl";
import { icon, node } from "~/util/dom";
import { store } from "~/state/store";

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

interface IDAY {
	date: string,
	interval: Array<Array<[number, number]>>;
	specificationId: string,
	specificationText: string;
}

// magie na sestaveni oteviracky X:XX-X:XX, X:XX-X:XX z dat [[[], []], [[], []]]
function buildDay(day: IDAY, source: string) {
	const interval = day.interval;

	if (!interval || !interval.length) {
		if (["nonstop", "phone_appointment"].includes(day.specificationId)) {
			return day.specificationText.replace(/^./u, match => match.toLowerCase());
		}

		if (day.specificationId === "closed" || !interval) {
			return _("detail.content.opening.close");
		}

		return source === "di" ? _("detail.content.opening.open") : _("detail.content.opening.nonstop");
	}

	return interval.map(inter => inter.map(time => time.flatMap((num, idx) => idx === 0
		? num
		: num
			? num.toString().padStart(2, "0")
			: []).join(":")).join("&ndash;")).join(", ");
}

export default class Opening extends Content {
	_seasonIndex: number;

	_seasons: Array<any>;

	_seasonsNotes: {
		[key: string]: HTMLParagraphElement;
	};

	_tooltipsClear: Array<() => void>;

	_log: (action: string, options: any) => void;

	constructor(data, options) {
		super(data, options);

		this._seasons = [];
		this._seasonsNotes = {};
		this._seasonIndex = -1;
		this._log = this._options.log;
		this._tooltipsClear = [];

		this._myBuild();
	}

	_myBuild() {
		const tDate = new Date(Date.now());
		const today = ((tDate.getMonth() + 1) * 100) + tDate.getDate();

		// dodatecny DOM spolecny vsem sezonam
		this._dom.container.classList.add("content-opening");

		if (!this._data.seasons) {
			return;
		}

		this._dom.notes = node("div", { className: "opening-notes" });
		this._dom.seasonCont = node("div", { className: "season-cont" });
		const deeplData = [];
		const seasonsLen = this._data.seasons.length;
		const actual = this._data.actual;
		const actualState = this._data.actualState;
		let msg = this._data.msg;

		if ((msg && !seasonsLen) || actual || actualState) {
			const title = node("p", { className: "title" });

			title.appendChild(icon("time"));
			let innerHTML = "";

			if (msg) {
				msg = msg.replace("\n", "<br>");
			}
			if (msg && !seasonsLen) {
				innerHTML = msg;
			} else {
				if (actualState) {
					innerHTML = `<span class="opening-state ${actualState.replace(/[A-Z]/u, match => "-".concat(match.toLowerCase()))}"> ${this._data.actualStateCaption}</span>`;
				}
				if (actual) {
					innerHTML += (actualState ? " - " : "") + actual;
				}
			}
			title.appendChild(node("span", {
				innerHTML,
				className: seasonsLen ? "today" : "note",
			}));
			this._dom.container.appendChild(title);

			if (seasonsLen) {
				title.addEventListener("click", this, false);
				title.classList.add("expandable");
				if (msg || this._data.holiday) {
					if (msg) {
						const note = node("p", { innerHTML: msg, className: "note" });

						deeplData.push({
							text: msg,
							lang: this._data.lang,
							container: note,
						});

						this._dom.notes.appendChild(note);
					}
					if (this._data.holiday) {
						const note = node("p", { innerHTML: this._data.holiday, className: "note" });

						deeplData.push({
							text: this._data.holiday,
							lang: this._data.lang,
							container: note,
						});

						this._dom.notes.appendChild(note);
					}

					this._dom.container.appendChild(this._dom.notes);
				}
			}
		}

		if (seasonsLen) {
			const options = [];
			let closestSeasonIndex = -1;

			for (let value = 0; value < this._data.seasons.length; value++) {
				const season = this._data.seasons[value];

				if (season.label) {
					options.push({ name: season.label, value });
				} else {
					const startDate = `${season.start % 100}. ${_("calendar.months.".concat(MONTHS[Math.floor(season.start / 100) - 1])).toLowerCase()}`;
					const endDate = `${season.end % 100}. ${_("calendar.months.".concat(MONTHS[Math.floor(season.end / 100) - 1])).toLowerCase()}`;

					options.push({ name: season.start !== 101 || season.end !== 1231 ? `${startDate} - ${endDate}` : "", value });
				}

				const seasonEl = this._buildSeason(season, tDate.getDay());

				if (season.msg /*&& season.msg !== this._data.msg*/) {
					this._seasonsNotes[value] = node("p", { innerHTML: season.msg, className: "note seasonal" });

					deeplData.push({
						text: season.msg,
						lang: this._data.lang,
						container: this._seasonsNotes[value],
					});

					this._dom.notes.appendChild(this._seasonsNotes[value]);
				}
				this._seasons.push(seasonEl);
				if (
					(
						season.end > season.start
						&& (season.start <= today && season.end >= today)
					) || (
						season.end < season.start && (
							(today >= 101 && today <= season.end)
							|| (today >= season.start && today <= 1231)
						)
					)
				) {
					this._seasonIndex = value;
				}
				if (season.end > today && closestSeasonIndex === -1) {
					closestSeasonIndex = value;
				}
			}
			if (this._seasonIndex === -1) {
				this._seasonIndex = closestSeasonIndex === -1 ? 0 : closestSeasonIndex;
			}
			if (options.length > 1) {
				// eslint-disable-next-line no-new
				new Dropdown({
					parent: this._dom.seasonCont,
					value: this._seasonIndex,
					options,
					timeout: 250,
					width: "calc(100% - 60px)",
					top: "8px",
					background: "#F0F0F0",
					padding: "4px 8px",
					gap: "8px",
					onChange: value => {
						this._log("click", { text: "openingHoursCalendarSelect" });
						this._seasonIndex = value;
						this._showSeason(this._seasonIndex);
					},
				});
				const gap = node("div", { className: "season-title" });

				this._dom.seasonCont.appendChild(gap);
			} else if (options.length) {
				const optionTitle = options[0].name;

				if (optionTitle.length) {
					const seasonTitle = node("div", { className: "season-title" });

					seasonTitle.appendChild(node("span", { innerHTML: optionTitle || " " }));
					this._dom.seasonCont.appendChild(seasonTitle);
				}
			}

			for (let sIdx = 0; sIdx < this._seasons.length; sIdx++) {
				this._dom.seasonCont.appendChild(this._seasons[sIdx]);
			}

			if (Object.keys(this._seasonsNotes).length && !this._dom.container.contains(this._dom.notes)) {
				this._dom.container.appendChild(this._dom.notes);
			}

			this._showSeason(this._seasonIndex);
		}

		this._dom.container.appendChild(this._dom.seasonCont);
		if (actualState === "unknown" || !Object.keys(this._data.seasons[this._seasonIndex]?.days || {}).length) {
			const cont = node("div", { className: "unknown-cont suggest-edit-cont" });

			cont.appendChild(node("p", { className: "desc", textContent: _("detail.content.opening.unknown.message") }));
			const btnSuggestEdit = node("button", { className: "btn-suggest-edit", textContent: _("detail.content.closed.suggestEdit") });

			btnSuggestEdit.addEventListener("click", () => {
				this._log("click", { text: "unknown_open_hours" });

				const { data, mark } = store.getState().detail;

				isPointInStatePolygon(mark.lon, mark.lat, {
					cs: true,
				}).then(async isCzechia => {
					// uprava udaju
					reportAnalytics({
						text: "Navrhnout úpravu údajů",
						origin: "poi",
						type: "editPlace",
						source: data.source,
						cz: isCzechia ? 1 : 0,
					}, "error_report");

					await reportWaitForLogin();

					const report = new ReportForm(getSuggestDataOptions(data, () => reportAnalytics({
						text: "goToNextStep",
						origin: "poi",
						type: "editPlaceNextStep",
						source: data.source,
						cz: isCzechia ? 1 : 0,
					}, "error_report")));

					report.open();
				}, () => {});
			});
			cont.appendChild(btnSuggestEdit);
			this._dom.container.appendChild(cont);
		}

		if (deeplData.length) {
			const deepl = createDeepLTranslate(deeplData, "openingHours");

			deepl && this._dom.notes.appendChild(deepl);
		}
	}

	destroy() {
		super.destroy();

		if (this._tooltipsClear.length) {
			const layout = Mapy.getComponent("layout");

			for (const clear of this._tooltipsClear) {
				layout.unsubscribe("scroll", clear);
			}
			this._tooltipsClear = [];
		}
	}

	handleEvent(evnt) {
		if (evnt.target.closest(".content-opening p.expandable")) {
			const { data } = store.getState().detail;

			if (this._dom.container.classList.contains("expanded")) {
				this._log("click", { text: "openingHoursHide" });
			} else {
				this._log("click", { text: "openingHoursShow" });
			}
			this._dom.container.classList.toggle("expanded");

			if (isFirmFromPoi(data)) {
				Mapy.getComponent("detail").sendFirmStats("opening", data, { placeId: 1, typeId: 30 });
			}
		}
	}

	_showSeason(index) {
		this._seasonIndex = index;
		for (let sIdx = 0; sIdx < this._seasons.length; sIdx++) {
			if (this._seasonsNotes[sIdx]) {
				this._seasonsNotes[sIdx].classList.remove("active");
			}
			this._seasons[sIdx].classList.remove("active");
			if (sIdx === index) {
				if (this._seasonsNotes[sIdx] && this._data.seasons[sIdx].msg !== this._data.msg) {
					this._seasonsNotes[sIdx].classList.add("active");
				}
				this._seasons[sIdx].classList.add("active");
			}
		}
	}

	_buildSeason(season, today) {
		const seasonCont = node("div", { className: "season" });


		const table = node("div", { className: "table" });
		const daysColumn = node("div", { className: "column" });
		const openingColumn = node("div", { className: "column hours" });
		const infoColumn = node("div", { className: "column" });

		// mame aspon jeden den vyplneny
		if (Object.keys(season.days).length) {
			for (let idx = 1; idx < 8; idx++) {
				const day = season.days[idx];
				const dayCell = node("div", { className: "cell", innerHTML: _(`detail.content.bigDays.nominativ.${idx % 7}`) });

				daysColumn.appendChild(dayCell);
				const openingCell = node("div", { className: "cell" });

				openingCell.innerHTML = day ? buildDay(day, this._options.source) : _("detail.content.opening.close");
				openingColumn.appendChild(openingCell);
				if (idx % 7 === today) {
					dayCell.classList.add("today");
					openingCell.classList.add("today");
				}
				if (!day || day.specificationId === "closed") {
					dayCell.classList.add("closed");
					openingCell.classList.add("closed");
				}

				let noInfo = true;

				if (day) {
					if (day.tooltipId || day.exceptionId || day.msg) {
						openingCell.classList.add("not-overflow");
						const info = node("div", { className: "cell info" });
						const textArray = [];

						if (day.exceptionText) {
							textArray.push(day.exceptionText);
						}
						if (day.msg) {
							textArray.push(day.msg);
						}
						if (day.tooltipText) {
							textArray.push(day.tooltipText);
						}
						const text = textArray.join("<br>");

						if (text.length) {
							const trigger = node("div", { className: "trigger" });

							trigger.appendChild(icon("square-info", text));
							info.appendChild(trigger);
							const tooltip = new Tooltip({
								anchor: trigger,
								trigger,
								triggerEventType: "pointerenter",
								content: node("p", { innerHTML: text }),
								arrow: true,
								flow: ETooltipFlow.ARROW_END,
								offset: [0, 0, -2, 0],
								className: "search-form__tooltip",
								hideTimeout: 0,
								onShow: () => {
									this._log("opening_hours_info_show", {});
								},
							});
							const clear = () => tooltip.hide();

							Mapy.getComponent("layout").subscribe("scroll", clear);
							this._tooltipsClear.push(clear);
							infoColumn.appendChild(info);
							noInfo = false;
						}
					}
				}

				if (noInfo) {
					infoColumn.appendChild(node("div", { className: "cell" }));
				}
			}
		// neni nic, jen mozna poznamka
		} else {
			const daysCell = node("div", { className: "cell today", innerHTML: _("detail.content.opening.allWeek") });
			const openingCell = node("div", { className: "cell today", innerHTML: _(this._data.actualState === "unknown" ? this._data.actualStateCaption.replace(/^./u, match => match.toLowerCase()) : "detail.content.opening.close") });

			daysColumn.appendChild(daysCell);
			openingColumn.appendChild(openingCell);
		}
		table.appendChild(daysColumn);
		table.appendChild(openingColumn);
		table.appendChild(infoColumn);

		seasonCont.appendChild(table);

		return seasonCont;
	}
}
